/**
 * Public-viewer overlay: a single competitor's GPS polyline + video-coverage
 * shading + playhead marker. Mounted inside the existing `<MapContainer>`
 * so it can use react-leaflet's map context.
 *
 * Stretches of the trail where the session has uploaded video render solid;
 * GPS-only stretches render dashed. Clicking the polyline projects the click
 * onto the nearest segment, interpolates the UTC between the two bracketing
 * samples, and fires `onSeek(utc_ms)` so the parent's HLSPlayer scrubs to that
 * moment.
 */

import React, { useMemo } from 'react';

import { GpsSummary } from '../types';
import {
  cumulativeSMeters,
  interpolatePlayhead,
  isCovered,
  nearestPointOnPolyline,
  playheadDivIcon,
} from '../plugins/synced_viewer/util';

type LeafletModule = typeof import('leaflet');
type ReactLeafletModule = typeof import('react-leaflet');

export interface MapModules {
  L: LeafletModule;
  RL: ReactLeafletModule;
}

interface DeviceTrailOverlayProps {
  modules: MapModules;
  track: GpsSummary['track'];
  coverage: ReadonlyArray<readonly [number, number]>;
  currentUtcMs: number | null;
  onSeek: (utcMs: number) => void;
}

interface TrailRun {
  covered: boolean;
  positions: Array<[number, number]>;
  firstUtc: number;
}

const SOLID_OPTS = {
  color: '#34d399',
  weight: 4,
  opacity: 0.95,
};
const DASHED_OPTS = {
  color: '#34d399',
  weight: 3,
  opacity: 0.65,
  dashArray: '6 8',
};

/** Samples further apart than this are missing data, not movement — drawing a
 *  segment across the gap paints a straight chord over terrain the car never
 *  drove (telemetry holes from uplink loss). Split instead; no shared boundary
 *  point, or the connector comes right back. GPS cadence is ~1 Hz. */
const GAP_SPLIT_MS = 10_000;

function buildRuns(
  track: GpsSummary['track'],
  coverage: ReadonlyArray<readonly [number, number]>,
): TrailRun[] {
  if (track.length === 0) return [];
  const runs: TrailRun[] = [];
  let current: TrailRun | null = null;
  let prevTs: number | null = null;
  for (let i = 0; i < track.length; i += 1) {
    const p = track[i];
    const ts = p[2];
    const has = isCovered(ts, coverage);
    const gap = prevTs != null && ts - prevTs > GAP_SPLIT_MS;
    prevTs = ts;
    if (current == null || gap) {
      if (current) runs.push(current);
      current = { covered: has, positions: [[p[0], p[1]]], firstUtc: ts };
      continue;
    }
    if (has === current.covered) {
      current.positions.push([p[0], p[1]]);
    } else {
      // Boundary point belongs to both runs so the polyline stays visually
      // continuous across the dashed↔solid transition.
      current.positions.push([p[0], p[1]]);
      runs.push(current);
      current = { covered: has, positions: [[p[0], p[1]]], firstUtc: ts };
    }
  }
  if (current) runs.push(current);
  return runs;
}

export const DeviceTrailOverlay: React.FC<DeviceTrailOverlayProps> = ({
  modules,
  track,
  coverage,
  currentUtcMs,
  onSeek,
}) => {
  const { L, RL } = modules;
  const { Polyline, Marker } = RL as any;

  const runs = useMemo(() => buildRuns(track, coverage), [track, coverage]);

  const playhead = useMemo(
    () => (currentUtcMs == null ? null : interpolatePlayhead(track, currentUtcMs)),
    [currentUtcMs, track],
  );

  const arrowIcon = useMemo(
    () => (playhead ? playheadDivIcon(L, playhead.heading) : null),
    [L, playhead],
  );

  // Precompute the polyline geometry once per track so the click handler
  // doesn't re-walk it on every click. `coords` mirrors the lat/lon pairs
  // the <Polyline> renders; `cumS[i]` is meters-from-start at vertex i.
  const trackGeometry = useMemo(() => {
    const coords = track.map((p) => [p[0], p[1]] as [number, number]);
    return { coords, cumS: cumulativeSMeters(coords) };
  }, [track]);

  const polylineHandlers = useMemo(
    () => ({
      click: (e: any) => {
        const { lat, lng } = e.latlng;
        const { coords, cumS } = trackGeometry;
        const proj = nearestPointOnPolyline(coords, cumS, lat, lng);
        if (!proj) return;
        // Project onto the nearest SEGMENT (not the nearest vertex) and
        // interpolate the click's fraction along it to a UTC between the
        // two bracketing samples. This fixes two bugs in the old
        // vertex-nearest + 50 m cap: (1) a click in the middle of a long
        // RDP-simplified straight could be >50 m from either endpoint and
        // silently no-op; (2) on self-crossing / lap tracks the nearest
        // vertex by distance could belong to a different time, seeking to
        // the wrong moment.
        const i = proj.segIndex;
        const a = track[i];
        const b = track[i + 1] ?? a;
        const segLen = cumS[i + 1] - cumS[i];
        const f = segLen > 0 ? (proj.s_m - cumS[i]) / segLen : 0;
        const fClamped = f < 0 ? 0 : f > 1 ? 1 : f;
        onSeek(a[2] + (b[2] - a[2]) * fClamped);
      },
    }),
    [track, trackGeometry, onSeek],
  );

  return (
    <>
      {runs.map((run) => (
        <Polyline
          key={`run-${run.firstUtc}`}
          positions={run.positions}
          pathOptions={run.covered ? SOLID_OPTS : DASHED_OPTS}
          eventHandlers={polylineHandlers as any}
        />
      ))}
      {playhead && arrowIcon && <Marker position={playhead.pos} icon={arrowIcon} />}
    </>
  );
};

export default DeviceTrailOverlay;
